/**
 * Modal Sessions Épuisées
 * ========================
 * 
 * RÈGLES MOA PHASE 4 (21 Jan 2026):
 * - Design premium non-punitif
 * - Message positif "Vous avez bien profité"
 * - 3 packs: 30/73/150 sessions (depuis API)
 * - Intégration Stripe pour paiement
 * - B2C uniquement (B2B bloqués)
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Gift, Star, Crown, Calendar, Loader2, AlertTriangle } from 'lucide-react';
import { useSessionCredits } from '../../hooks/useSessionCredits';
import { getBackendUrl } from '../../config/environment';
import { toast } from 'sonner';
import { saveResumeState } from '../../utils/sessionResume';

const BACKEND_URL = getBackendUrl();

// Icons par pack
const PACK_ICONS = {
  'pack_10': Gift,      // Découverte 30 sessions
  'pack_25': Star,      // Standard 73 sessions
  'pack_50': Crown      // Pro 150 sessions
};

// Labels et badges
const PACK_META = {
  'pack_10': { label: 'Découverte', popular: false, discount: null },
  'pack_25': { label: 'Standard', popular: true, discount: null },
  'pack_50': { label: 'Pro', popular: false, discount: '-27%' }
};

export function SessionsExhaustedModal({ isOpen, onClose, onRecharged, resumeSnapshot }) {
  const { resetDate, current, refreshSilent } = useSessionCredits();
  const [packs, setPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState('pack_25');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPacks, setIsLoadingPacks] = useState(true);
  const [error, setError] = useState(null);
  const [awaitingPayment, setAwaitingPayment] = useState(false);  // popup ouverte, attente recharge

  // Référence stable au crédit initial à l'ouverture, pour détecter la recharge
  const baselineCreditsRef = useRef(0);

  // Polling silencieux : tant que la modal est ouverte ET awaitingPayment,
  // on poll session-credits toutes les 3s pour détecter la recharge.
  useEffect(() => {
    if (!isOpen || !awaitingPayment) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        await refreshSilent();
      } catch (_) { /* noop */ }
    };
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isOpen, awaitingPayment, refreshSilent]);

  // Détecte la recharge : current était 0 (baseline) et devient > baseline.
  useEffect(() => {
    if (!isOpen || !awaitingPayment) return;
    if (typeof current === 'number' && current > baselineCreditsRef.current) {
      // Recharge confirmée
      toast.success('Crédits rechargés. Ta session reprend exactement où tu en étais ✨');
      setAwaitingPayment(false);
      if (typeof onRecharged === 'function') onRecharged();
    }
  }, [current, awaitingPayment, isOpen, onRecharged]);

  // À l'ouverture de la modal : capture la baseline crédits.
  // (intentionnel: pas de dépendance `current` pour éviter de re-snapshoter
  //  la baseline à chaque tick de polling — on snapshote UNIQUEMENT à l'open).
  const currentRef = useRef(current);
  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => {
    if (isOpen) {
      baselineCreditsRef.current = typeof currentRef.current === 'number' ? currentRef.current : 0;
      setAwaitingPayment(false);
    }
  }, [isOpen]);

  // Charger les packs depuis l'API
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchPacks = async () => {
      setIsLoadingPacks(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/billing/session-packs`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.status === 403) {
          // Utilisateur B2B - pas de recharge possible
          setError('b2b_blocked');
          return;
        }
        
        if (!response.ok) {
          throw new Error('Erreur chargement packs');
        }
        
        const data = await response.json();
        setPacks(data);
      } catch (err) {
        console.error('Error fetching packs:', err);
        setError('load_error');
      } finally {
        setIsLoadingPacks(false);
      }
    };
    
    fetchPacks();
  }, [isOpen]);

  const handlePurchase = async () => {
    const pack = packs.find(p => p.id === selectedPack);
    if (!pack) return;
    
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/billing/create-recharge-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pack_id: selectedPack,
          origin_url: window.location.origin
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur création paiement');
      }
      
      const data = await response.json();

      if (!data.url) {
        throw new Error('URL de paiement manquante');
      }

      // 🛟 Persister le snapshot de session AVANT toute redirection,
      //    pour repositionner l'utilisateur sur l'échange en cours au retour.
      saveResumeState(resumeSnapshot || { pathname: window.location.pathname });

      // 🪟 Tenter d'ouvrir Stripe en popup (non destructif).
      //    Si bloqué (Safari mobile, browsers stricts) → fallback redirect.
      let popup = null;
      try {
        popup = window.open(
          data.url,
          'coachdia_stripe_checkout',
          'width=480,height=720,menubar=no,toolbar=no,location=yes,status=yes,resizable=yes,scrollbars=yes',
        );
      } catch (_) {
        popup = null;
      }

      const popupOk = popup && !popup.closed && typeof popup.focus === 'function';
      if (popupOk) {
        try { popup.focus(); } catch (_) { /* noop */ }
        setAwaitingPayment(true);  // déclenche le polling
      } else {
        // Fallback : redirect full-page. Le snapshot sessionStorage permettra
        // à PaymentSuccess de rediriger vers la session d'origine.
        window.location.href = data.url;
      }
      
    } catch (err) {
      console.error('Purchase error:', err);
      toast.error(err.message || 'Erreur lors du paiement');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const selected = packs.find(p => p.id === selectedPack);

  // 🎨 Palette premium ivory/violet/gold — alignée sur le design system
  const T = {
    bg: '#FBF9F3',
    card: '#FFFFFF',
    ink: '#1C1147',
    text: '#1F2937',
    muted: '#6B7280',
    border: '#E8E2D2',
    gold: '#D4AF37',
    goldDeep: '#B7901F',
    goldLight: '#F3D060',
    ivoryDeep: '#FFFCEC',
    violet: '#7A4FCF',
    violetSoft: '#F4EFFF',
    green: '#48825A',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      data-testid="sessions-exhausted-modal"
    >
      {/* Backdrop premium */}
      <div
        className="fixed inset-0"
        style={{
          background: 'rgba(28,17,71,0.55)',
          backdropFilter: 'blur(10px)',
        }}
        onClick={onClose}
      />

      {/* Modal — fond ivoire lumineux */}
      <div
        className="relative rounded-3xl max-w-md w-full max-h-[90vh] overflow-hidden my-4 flex flex-col"
        style={{
          background: T.bg,
          border: `1px solid ${T.border}`,
          boxShadow: '0 30px 80px -20px rgba(28,17,71,0.5), 0 0 0 1px rgba(212,175,55,0.15)',
        }}
      >
        {/* Halo or top */}
        <div
          className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(243,208,96,0.35) 0%, transparent 70%)',
          }}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full transition-colors z-10 hover:bg-black/5"
          data-testid="close-recharge-modal"
        >
          <X className="w-5 h-5" style={{ color: T.muted }} />
        </button>

        {/* Contenu scrollable */}
        <div className="relative p-6 sm:p-8 overflow-y-auto flex-1">
          {/* Header */}
          <div className="text-center mb-7">
            <div
              className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                boxShadow: `0 8px 24px -10px ${T.gold}aa`,
              }}
            >
              <Sparkles className="w-8 h-8" style={{ color: T.ink }} />
            </div>
            <h2
              className="text-2xl mb-2"
              style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 600 }}
            >
              {awaitingPayment ? 'Session suspendue' : 'Sessions épuisées'}
            </h2>
            <p style={{ color: T.muted }} className="text-sm">
              {awaitingPayment ? (
                <>
                  Ta session est <strong style={{ color: T.ink }}>en pause</strong> — pas de panique, ta conversation est conservée.<br />
                  Termine ton paiement Stripe puis reviens : tu reprendras exactement là où vous vous êtes arrêtés.
                </>
              ) : (
                <>
                  Tu as bien profité de COACHDIA !<br />
                  Recharge pour <strong style={{ color: T.ink }}>reprendre exactement là où tu en étais</strong>.
                </>
              )}
            </p>
            {awaitingPayment && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: T.ivoryDeep, border: `1px solid ${T.border}` }} data-testid="awaiting-payment-indicator">
                <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: T.violet }} />
                <span className="text-xs" style={{ color: T.muted }}>
                  En attente de confirmation du paiement…
                </span>
              </div>
            )}
          </div>

          {/* État: Chargement */}
          {isLoadingPacks && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: T.violet }} />
            </div>
          )}

          {/* État: Utilisateur B2B */}
          {error === 'b2b_blocked' && (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: T.goldDeep }} />
              <p className="mb-2" style={{ color: T.ink, fontWeight: 600 }}>Recharge non disponible</p>
              <p className="text-sm" style={{ color: T.muted }}>
                Votre quota de sessions est géré par votre entreprise.<br />
                Contactez votre administrateur si nécessaire.
              </p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2 rounded-full transition-colors text-sm font-semibold"
                style={{ background: T.card, color: T.ink, border: `1px solid ${T.border}` }}
              >
                Compris
              </button>
            </div>
          )}

          {/* État: Erreur de chargement */}
          {error === 'load_error' && (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: '#9B2C1F' }} />
              <p className="mb-4" style={{ color: T.ink, fontWeight: 600 }}>Impossible de charger les packs</p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 rounded-full text-sm font-semibold"
                style={{
                  background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                  color: T.ink,
                }}
              >
                Réessayer
              </button>
            </div>
          )}

          {/* État: Packs chargés */}
          {!isLoadingPacks && !error && packs.length > 0 && (
            <>
              {/* Packs */}
              <div className="space-y-3 mb-6">
                {packs.map((pack) => {
                  const Icon = PACK_ICONS[pack.id] || Gift;
                  const meta = PACK_META[pack.id] || { label: pack.name, popular: false, discount: null };
                  const isSelected = selectedPack === pack.id;

                  return (
                    <button
                      key={pack.id}
                      onClick={() => setSelectedPack(pack.id)}
                      className="w-full p-4 rounded-2xl transition-all duration-200 text-left relative"
                      style={
                        isSelected
                          ? {
                              background: `linear-gradient(135deg, ${T.ivoryDeep} 0%, ${T.card} 60%, ${T.ivoryDeep} 100%)`,
                              border: `2px solid ${T.gold}`,
                              boxShadow: `0 14px 32px -14px ${T.gold}80, inset 0 1px 0 rgba(255,255,255,0.8)`,
                            }
                          : {
                              background: T.card,
                              border: `1.5px solid ${T.border}`,
                              boxShadow: '0 2px 6px -2px rgba(28,17,71,0.06)',
                            }
                      }
                      data-testid={`pack-option-${pack.id}`}
                    >
                      {/* Popular badge */}
                      {meta.popular && (
                        <span
                          className="absolute -top-2.5 right-4 px-3 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wider"
                          style={{
                            background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                            color: T.ink,
                            boxShadow: `0 4px 10px -4px ${T.gold}80`,
                          }}
                        >
                          Populaire
                        </span>
                      )}

                      {/* Discount badge */}
                      {meta.discount && (
                        <span
                          className="absolute -top-2.5 right-4 px-3 py-0.5 text-[10px] font-bold rounded-full"
                          style={{
                            background: T.green,
                            color: '#FFFFFF',
                            boxShadow: `0 4px 10px -4px ${T.green}80`,
                          }}
                        >
                          {meta.discount}
                        </span>
                      )}

                      <div className="flex items-center gap-4">
                        {/* Radio */}
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center transition-all"
                          style={{
                            border: `2px solid ${isSelected ? T.goldDeep : T.border}`,
                            background: isSelected ? T.ivoryDeep : 'transparent',
                          }}
                        >
                          {isSelected && (
                            <div
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ background: T.goldDeep }}
                            />
                          )}
                        </div>

                        {/* Icon */}
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center transition-all"
                          style={{
                            background: isSelected
                              ? `linear-gradient(135deg, ${T.goldLight}88, ${T.gold}55)`
                              : T.violetSoft,
                          }}
                        >
                          <Icon
                            className="w-5 h-5"
                            style={{ color: isSelected ? T.goldDeep : T.violet }}
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span
                              className="font-bold"
                              style={{
                                color: T.ink,
                                fontFamily: 'Georgia, serif',
                                fontSize: '15px',
                              }}
                            >
                              {meta.label}
                            </span>
                            <span className="text-sm" style={{ color: T.muted }}>
                              {pack.sessions} sessions
                            </span>
                          </div>
                          <span className="text-xs" style={{ color: T.muted }}>
                            {pack.price_per_session.toFixed(2)}€/session
                          </span>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <span
                            className="text-xl font-bold"
                            style={{
                              color: T.ink,
                              fontFamily: 'Georgia, serif',
                            }}
                          >
                            {pack.price.toFixed(2)}€
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* CTA */}
              <button
                onClick={handlePurchase}
                disabled={isLoading || !selected}
                className="w-full py-4 rounded-full font-bold text-base transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                  color: T.ink,
                  boxShadow: `0 12px 30px -10px ${T.gold}aa`,
                }}
                data-testid="purchase-sessions-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Redirection vers le paiement...
                  </>
                ) : (
                  <>
                    Recharger {selected?.sessions} sessions — {selected?.price.toFixed(2)}€
                  </>
                )}
              </button>
            </>
          )}

          {/* Alternative - attendre le renouvellement */}
          {!error && (
            <div className="mt-4 text-center">
              <button
                onClick={onClose}
                className="text-sm flex items-center gap-2 mx-auto transition-colors hover:underline"
                style={{ color: T.muted }}
                data-testid="wait-renewal-btn"
              >
                <Calendar className="w-4 h-4" />
                Attendre le renouvellement le {resetDate}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SessionsExhaustedModal;
